/** State model of the booking batch-edit dialog.
 *
 *  One block = one field (at most once per run). Blocks carry stable ids — never
 *  key a block by its array index: removing a middle block would re-bind the
 *  pickers' internal state to the wrong block. The wire rules mirror the
 *  backend validator (apiv2/lib/console/booking_edit/validate.py) so the dialog
 *  never offers a request the server would refuse:
 *
 *   - update events: explicit Emit / Suppress, no default;
 *   - booking state Cancelled → a cancellation mode;
 *   - the start can move (tour / date / departure time) and the end isn't set
 *     explicitly → move or keep booking-specific ends (no default);
 *   - a tour block → drop or keep the old tour's own tag categories;
 *   - an order ref block → move to the order with the new ref, or keep;
 *   - a bespoke target tour → Date / Departure time blocks are disabled and
 *     not sent (the backend refuses the combination).
 */
import { format } from 'date-fns';

import type {
  BookingEditBlockWire,
  BookingEditCatalog,
  BookingEditCatalogEffect,
  BookingEditCatalogField,
  BookingEditOp,
  BookingEditParams,
  BookingEditRoleOption,
  BookingEditTourOption,
  CancelMode,
  UpdateEvents,
} from '@/api/bookingEdit';

export const STATE_CANCELLED = 2;
export const END_TYPE_ITINERARY = 0;
export const BESPOKE_BLOCKED_FIELDS = ['date', 'departure_time'] as const;
export const START_FIELDS = ['tour', 'date', 'departure_time'] as const;

export interface BlockDraft {
  id: string;
  field: string | null;
  op: BookingEditOp;
  tour: BookingEditTourOption | null;
  role: BookingEditRoleOption | null;
  /** Role block: clear the role (guest). */
  roleClear: boolean;
  /** Wall-clock value of a date / time / datetime picker. */
  when: Date | null;
  /** Shift amount as typed (days or minutes). */
  shift: string;
  enumValue: number | null;
  text: string;
  integer: string;
  bool: boolean | null;
  cancelMode: CancelMode | null;
}

export interface RunOptions {
  updateEvents: UpdateEvents | null;
  customEnd: 'move' | 'keep' | null;
  oldTourTags: 'drop' | 'keep' | null;
  orderLink: 'move' | 'keep' | null;
}

let _seq = 0;
export function newBlock(): BlockDraft {
  _seq += 1;
  return {
    id: `be-${_seq}`,
    field: null,
    op: 'set',
    tour: null,
    role: null,
    roleClear: false,
    when: null,
    shift: '',
    enumValue: null,
    text: '',
    integer: '',
    bool: null,
    cancelMode: null,
  };
}

/** Reset the value parts of a block when its field changes. */
export function withField(b: BlockDraft, field: string, catalog: BookingEditCatalog): BlockDraft {
  const fresh = newBlock();
  return {
    ...fresh,
    id: b.id,
    field,
    cancelMode: field === 'booking_state' ? catalog.options.cancel_mode_default : null,
  };
}

export function fieldOf(catalog: BookingEditCatalog | undefined, key: string | null) {
  return key ? catalog?.fields.find((f) => f.key === key) ?? null : null;
}

/** The target tour when a Tour block picked a bespoke one (disables date/time). */
export function bespokeTarget(blocks: BlockDraft[]): BookingEditTourOption | null {
  const t = blocks.find((b) => b.field === 'tour')?.tour ?? null;
  return t?.is_bespoke ? t : null;
}

/** Blocks that are shown but not sent (and why). */
export function disabledReason(b: BlockDraft, blocks: BlockDraft[]): string | null {
  if (b.field && (BESPOKE_BLOCKED_FIELDS as readonly string[]).includes(b.field) && bespokeTarget(blocks)) {
    return 'The target tour is bespoke: its bookings take the tour’s own start date and time, so this block is disabled and will not be sent.';
  }
  return null;
}

function parseIntStrict(s: string): number | null {
  if (!/^-?\d+$/.test(s.trim())) return null;
  return Number.parseInt(s.trim(), 10);
}

/** The block's wire value, or `undefined` while incomplete/invalid. */
export function blockValue(spec: BookingEditCatalogField, b: BlockDraft): unknown {
  if (b.op === 'shift') {
    const n = parseIntStrict(b.shift);
    return n == null || n === 0 ? undefined : n;
  }
  switch (spec.widget) {
    case 'tour':
      return b.tour ? { id: b.tour.id } : undefined;
    case 'role':
      if (b.roleClear) return null;
      return b.role ? { id: b.role.id } : undefined;
    case 'date':
      return b.when ? format(b.when, 'yyyy-MM-dd') : undefined;
    case 'time':
      return b.when ? format(b.when, 'HH:mm') : undefined;
    case 'datetime':
      return b.when ? format(b.when, "yyyy-MM-dd'T'HH:mm") : undefined;
    case 'enum':
      return b.enumValue ?? undefined;
    case 'text': {
      const t = b.text.trim();
      if (!t) return spec.nullable ? null : undefined;
      if (spec.max_length != null && t.length > spec.max_length) return undefined;
      return t;
    }
    case 'integer': {
      const n = parseIntStrict(b.integer);
      if (n == null) return undefined;
      if (spec.min_value != null && n < spec.min_value) return undefined;
      if (spec.max_value != null && n > spec.max_value) return undefined;
      return n;
    }
    case 'boolean':
      return b.bool ?? undefined;
    default:
      return undefined;
  }
}

export function blockComplete(catalog: BookingEditCatalog, b: BlockDraft): boolean {
  const spec = fieldOf(catalog, b.field);
  if (!spec) return false;
  if (blockValue(spec, b) === undefined) return false;
  if (b.field === 'booking_state' && b.enumValue === STATE_CANCELLED && !b.cancelMode) return false;
  return true;
}

/** Blocks that will be sent (a field chosen, not disabled). */
export function activeBlocks(blocks: BlockDraft[]): BlockDraft[] {
  return blocks.filter((b) => b.field != null && disabledReason(b, blocks) == null);
}

export interface Requirements {
  cancelMode: boolean;
  customEnd: boolean;
  oldTourTags: boolean;
  orderLink: boolean;
}

export function requirements(blocks: BlockDraft[]): Requirements {
  const active = activeBlocks(blocks);
  const has = (k: string) => active.some((b) => b.field === k);
  const endTypeItinerary = active.some((b) => b.field === 'end_ts_type' && b.enumValue === END_TYPE_ITINERARY);
  return {
    cancelMode: active.some((b) => b.field === 'booking_state' && b.enumValue === STATE_CANCELLED),
    customEnd: START_FIELDS.some(has) && !has('end_ts') && !endTypeItinerary,
    oldTourTags: has('tour'),
    orderLink: has('order_ref'),
  };
}

/** Tenant ids of the tenant-scoped values (tour, role) — must be one (D6). */
export function scopedTenantIds(blocks: BlockDraft[]): number[] {
  const ids = new Set<number>();
  for (const b of activeBlocks(blocks)) {
    if (b.field === 'tour' && b.tour?.tenant?.id != null) ids.add(b.tour.tenant.id);
    if (b.field === 'role' && !b.roleClear && b.role?.tenant?.id != null) ids.add(b.role.tenant.id);
  }
  return [...ids];
}

export function buildParams(
  catalog: BookingEditCatalog,
  blocks: BlockDraft[],
  opts: RunOptions,
  reason: string,
  dryRun: boolean,
): BookingEditParams {
  const active = activeBlocks(blocks);
  const req = requirements(blocks);
  const wire: BookingEditBlockWire[] = [];
  for (const b of active) {
    const spec = fieldOf(catalog, b.field);
    if (!spec) continue;
    const value = blockValue(spec, b);
    wire.push({ field: spec.key, ...(b.op === 'shift' ? { op: 'shift' as const } : {}), value });
  }
  const cancel = active.find((b) => b.field === 'booking_state' && b.enumValue === STATE_CANCELLED);
  const options: BookingEditParams['options'] = {};
  if (req.customEnd && opts.customEnd) options.custom_end = opts.customEnd;
  if (req.oldTourTags && opts.oldTourTags) options.old_tour_tags = opts.oldTourTags;
  if (req.orderLink && opts.orderLink) options.order_link = opts.orderLink;
  return {
    dry_run: dryRun,
    reason: reason.trim(),
    events: {
      update_events: opts.updateEvents ?? 'suppress',
      ...(cancel?.cancelMode ? { cancel_mode: cancel.cancelMode } : {}),
    },
    options,
    blocks: wire,
  };
}

/** Why the run can't be submitted yet (first reason), or null when ready. */
export function submitBlocker(
  catalog: BookingEditCatalog,
  blocks: BlockDraft[],
  opts: RunOptions,
  reason: string,
): string | null {
  const active = activeBlocks(blocks);
  if (active.length === 0) return 'Add at least one field.';
  if (!active.every((b) => blockComplete(catalog, b))) return 'Complete every field block.';
  if (scopedTenantIds(blocks).length > 1) return 'The tour and the role belong to different tenants.';
  if (!opts.updateEvents) return 'Choose whether update events are emitted or suppressed.';
  const req = requirements(blocks);
  if (req.customEnd && !opts.customEnd) return 'Choose what happens to booking-specific end datetimes.';
  if (req.oldTourTags && !opts.oldTourTags) return 'Choose what happens to the old tour’s tag categories.';
  if (req.orderLink && !opts.orderLink) return 'Choose whether bookings move to the order with the new ref.';
  if (reason.trim().length < catalog.min_reason) return `Give a reason of at least ${catalog.min_reason} characters.`;
  return null;
}

export interface EffectPreview {
  flushed: BookingEditCatalogEffect[];
  automatic: BookingEditCatalogEffect[];
}

/** Caches & syncs the configured blocks declare (live, before any dry run). */
export function previewEffects(catalog: BookingEditCatalog, blocks: BlockDraft[]): EffectPreview {
  const codes = new Set<string>();
  const auto = new Set<string>();
  for (const b of activeBlocks(blocks)) {
    const spec = fieldOf(catalog, b.field);
    if (!spec) continue;
    spec.effects.forEach((c) => codes.add(c));
    spec.automatic.forEach((c) => auto.add(c));
    if (b.field === 'booking_state' && b.enumValue !== STATE_CANCELLED) auto.delete('cancel_chain');
    if (b.field === 'booking_state' && b.enumValue === STATE_CANCELLED && b.cancelMode === 'silent_state') {
      auto.delete('cancel_chain');
    }
  }
  const byCode = new Map(catalog.effects.map((e) => [e.code, e]));
  const pick = (set: Set<string>) =>
    catalog.effects.filter((e) => set.has(e.code)).map((e) => byCode.get(e.code) as BookingEditCatalogEffect);
  return { flushed: pick(codes), automatic: pick(auto) };
}

/** Everything that identifies the operation — a change voids a shown dry run. */
export function operationIdentity(
  catalog: BookingEditCatalog | undefined,
  blocks: BlockDraft[],
  opts: RunOptions,
  selection: unknown,
): string {
  if (!catalog) return '';
  const p = buildParams(catalog, blocks, opts, '', true);
  return JSON.stringify({ selection, blocks: p.blocks, events: p.events, options: p.options });
}
